/* Top-bar approvals inbox (beside the alert bell).
   Maintenance logs save as "pending" and count NOWHERE until approved here.
   Visible/usable ONLY for plant_head and production_manager (owner rule);
   the backend enforces the same roles, this is just the entry point. */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

export default function MaintenanceApprovals() {
  // RBAC-driven: the Maintenance "Approve" permission (RBAC Settings column)
  // decides who sees this inbox. The SERVER is asked directly (one light call)
  // instead of trusting the login-cached permissions — so a permission granted
  // after login shows the button without re-logging-in, and a revoked one
  // hides it. A 403 stops further polling for the session.
  const { user } = useAuth();
  const [allowed, setAllowed] = useState(false);
  const deniedRef = useRef(false);
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/maintenance/pending-approvals", { silent403: true });
      deniedRef.current = false;
      setAllowed(true);
      setRows(r.data.records || []);
      setCount(r.data.count || 0);
    } catch (e) {
      if (e.response?.status === 403) { deniedRef.current = true; setAllowed(false); }
      /* other errors: keep whatever we had */
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    deniedRef.current = false;
    load();
    const t = setInterval(() => { if (!deniedRef.current) load(); }, 120000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const decide = async (id, action) => {
    setBusy(id);
    try {
      await api.post(`/maintenance/${id}/approval`, { action });
      toast.success(action === "approve"
        ? "Approved — the log now counts in all metrics"
        : "Rejected — the log stays recorded but counts nowhere");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save decision");
    } finally {
      setBusy(null);
    }
  };

  if (!allowed) return null;

  return (
    <>
      <Button variant="ghost" size="icon" className="relative mr-1"
              onClick={() => { setOpen(true); load(); }}
              title="Maintenance logs waiting for your approval"
              data-testid="approvals-btn">
        <ClipboardCheck className="w-5 h-5" />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center"
                data-testid="approvals-count-badge">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-amber-600" />
              Maintenance approvals ({count} pending)
            </DialogTitle>
          </DialogHeader>
          {!rows.length ? (
            <p className="text-sm text-muted-foreground py-4">
              Nothing waiting — every maintenance log is approved.
            </p>
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="rounded-lg border p-3 text-xs space-y-1"
                     data-testid={`approval-row-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-semibold">
                      {[r.customer_name, r.part_name, r.operation_name].filter(Boolean).join(" · ") || "(operation removed)"}
                    </span>
                    <Badge className="text-[10px] bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 uppercase">
                      {r.maintenance_type}
                    </Badge>
                  </div>
                  {r.description && <p className="text-muted-foreground">{r.description}</p>}
                  <p className="text-[10px] text-muted-foreground">
                    Opened {String(r.opened_at || "").replace("T", " ")} · status {r.status}
                    {r.technician ? ` · technician ${r.technician}` : ""}
                    {" · logged by "}{r.created_by_name || r.created_by || "—"}
                  </p>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            disabled={busy === r.id} onClick={() => decide(r.id, "approve")}
                            data-testid={`approve-${r.id}`}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline"
                            className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                            disabled={busy === r.id} onClick={() => decide(r.id, "reject")}
                            data-testid={`reject-${r.id}`}>
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
